import React, { useEffect, useState } from 'react';
import {
  Create,
  SimpleForm,
  TextInput,
  ReferenceInput,
  SelectInput,
  useDataProvider,
  useNotify,
} from 'react-admin';
import { useFormContext } from 'react-hook-form';
import { Box, Typography, CircularProgress, Alert } from '@mui/material';

const AutoTableResolver: React.FC = () => {
  const { watch, setValue } = useFormContext();
  const plantId = watch('plantId');
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [loading, setLoading] = useState(false);
  const [zoneName, setZoneName] = useState<string | null>(null);
  const [tableName, setTableName] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const resolveTable = async () => {
      if (!plantId) {
        setZoneName(null);
        setTableName(null);
        setValue('tableId', undefined);
        setError(null);
        return;
      }
      setLoading(true);
      setError(null);
      setZoneName(null);
      setTableName(null);
      setValue('tableId', undefined);
      try {
        // 1. Obtener zonas de la planta
        const zonesResp = await dataProvider.getList('zones', {
          pagination: { page: 1, perPage: 50 },
          sort: { field: 'zoneName', order: 'ASC' },
          filter: { plantId: plantId },
        });
        const firstZone = zonesResp.data?.[0];
        if (!firstZone) {
          setError('La planta seleccionada no tiene zonas disponibles');
          notify('La planta seleccionada no tiene zonas', { type: 'warning' });
          return;
        }
        setZoneName(firstZone.zoneName);

        // 2. Obtener tablas de la primera zona
        const tablesResp = await dataProvider.getList('tables', {
          pagination: { page: 1, perPage: 50 },
          sort: { field: 'tableName', order: 'ASC' },
          filter: { zoneId: firstZone.id },
        });
        const firstTable = tablesResp.data?.[0];
        if (!firstTable) {
          setError('La primera zona no tiene tablas disponibles');
          notify('La primera zona no tiene tablas', { type: 'warning' });
          return;
        }
        setTableName(firstTable.tableName);
        setValue('tableId', firstTable.id, { shouldDirty: true });
      } catch (e) {
        console.error('Error resolviendo primera tabla:', e);
        setError('Error cargando zonas/tablas');
        notify('Error cargando zonas/tablas', { type: 'error' });
      } finally {
        setLoading(false);
      }
    };
    resolveTable();
  }, [plantId, dataProvider, notify, setValue]);

  return (
    <Box sx={{ mt: 2 }}>
      {loading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CircularProgress size={18} />
          <Typography variant='body2'>
            Buscando primera tabla disponible...
          </Typography>
        </Box>
      )}
      {!loading && error && <Alert severity='warning'>{error}</Alert>}
      {!loading && !error && plantId && zoneName && tableName && (
        <Typography variant='body2' color='text.secondary'>
          Zona seleccionada automáticamente: <strong>{zoneName}</strong> | Tabla
          asignada: <strong>{tableName}</strong>
        </Typography>
      )}
    </Box>
  );
};

const DevicesCreate = (props: any) => (
  <Create
    {...props}
    transform={(data: any) => {
      // Eliminar plantId del payload si el backend no lo necesita
      const { plantId, ...rest } = data;
      return rest;
    }}
  >
    <SimpleForm>
      <TextInput source='variableName' required />
      <SelectInput
        source='deviceType'
        label='Tipo de Dispositivo'
        choices={[
          { id: 'DEVICE', name: 'DEVICE' },
          { id: 'PLC', name: 'PLC' },
        ]}
        required
      />
      <TextInput source='ipAddress' required />
      {/* Selector de planta */}
      <ReferenceInput
        source='plantId'
        reference='plants'
        sort={{ field: 'plantName', order: 'ASC' }}
        perPage={100}
      >
        <SelectInput
          optionText='plantName'
          optionValue='id'
          label='Planta'
          required
        />
      </ReferenceInput>
      {/* Campo oculto tableId (asignado automáticamente) */}
      <TextInput source='tableId' style={{ display: 'none' }} />
      <AutoTableResolver />
    </SimpleForm>
  </Create>
);

export default DevicesCreate;
